import React, { useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { taxMetaService } from 'api/services';
import type { JurisdictionFieldSchemaDto, RegistrationNumberParseDto, TaxFieldContextDto } from 'api/types';

// ============================================================================
// Jurisdiction-driven tax fields (ADR-0019; generalised by ADR-0047 Phase 2 §7).
//
// Renders the fields a jurisdiction declares for a target (REGISTRATION / PARTY /
// SERVICE / DOCUMENT / LINE) — so "VAT Number" vs "GSTIN", a state picker present
// or absent, etc. are DATA, not code. Values are keyed by field_key; the host
// screen sends them to the API as-is and the server validates them against the
// same schema (required, pattern, static options). `validateTaxFields` mirrors
// the server rules so the user sees the refusal before the round-trip.
//
// Option sources (schema `optionSource`):
//   ORG.STATE_PROVINCE      context.stateProvinces (value = subdivision id)
//   PACK.SUPPLY_TYPES       context.supplyTypes    (value = supply code)
//   TAX.UNITS               context.units          (value = unit code)
//   TAX.CLASSIFICATION      searched asynchronously through `asyncSources`
//                           (value = classification code; free entry unless enforced)
//   STATIC:a|b|c            the literal list
//   legacy TAX_RATE / SUPPLY_TYPE / SUPPLY_CATEGORY (seed 104) kept as before
//
// Derived fields (schema `derivedFrom`): under a regime whose registration number
// encodes the place of registration (India GST: the first two characters), the
// state field is derived from the number field. While the number is filled the
// state is shown read-only as the number resolves it ("29 — Karnataka", or why it
// does not resolve); while the number is empty the field is the ordinary picker
// (an unregistered client still has a state). The server derives the same on save.
// ============================================================================

export type TaxFieldValues = Record<string, unknown>;

/** A searched option source: `load` runs the search, `labelFor` renders a stored value before any search ran. */
export interface AsyncOptionSource {
  load: (input: string) => Promise<SelectOption[]>;
  labelFor?: (value: string) => string | undefined;
  /** Allow a value the search did not return (TAX.CLASSIFICATION when not enforced). */
  allowFreeEntry?: boolean;
}

interface Props {
  context: TaxFieldContextDto | null;
  values: TaxFieldValues;
  onChange: (key: string, value: unknown) => void;
  /** Field-level errors keyed by field_key (from validateTaxFields or the server). */
  errors?: Record<string, string>;
  /** Show only these keys (default: every field of the context). */
  onlyKeys?: string[];
  /** Hide the "Tax · Jurisdiction" caption when the panel is the whole form. */
  bare?: boolean;
  disabled?: boolean;
  /** Searched option sources keyed by option source (upper-case), e.g. 'TAX.CLASSIFICATION'. */
  asyncSources?: Record<string, AsyncOptionSource>;
  /** How a derived field's source number is parsed. Default: under the header entity's jurisdiction.
   *  A form that runs before the entity holds a jurisdiction (creating an entity) passes its own. */
  parseNumber?: (number: string) => Promise<RegistrationNumberParseDto>;
}

function staticValues(src?: string | null): string[] | null {
  if (!src || !src.toUpperCase().startsWith('STATIC:')) return null;
  return src.slice('STATIC:'.length).split('|').map(s => s.trim()).filter(Boolean);
}

export function optionsForField(context: TaxFieldContextDto, f: JurisdictionFieldSchemaDto): SelectOption[] {
  const src = (f.optionSource || '').toUpperCase();
  const statics = staticValues(f.optionSource);
  if (statics) return statics.map(s => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') }));
  if (src === 'ORG.STATE_PROVINCE') {
    return (context.stateProvinces || []).map(s => ({
      value: s.id,
      label: s.taxStateCode ? `${s.provinceName} (${s.taxStateCode})` : s.provinceName,
    }));
  }
  if (src === 'PACK.SUPPLY_TYPES' || src === 'SUPPLY_TYPE') {
    return (context.supplyTypes || []).map(s => ({ value: s.supplyCode, label: s.supplyName }));
  }
  if (src === 'TAX.UNITS') {
    return (context.units || []).map(u => ({ value: u.unitCode, label: `${u.unitCode} — ${u.unitName}` }));
  }
  if (src === 'PACK.PLACES_OF_SUPPLY') {
    return (context.placesOfSupply || []).map(p => ({ value: p.placeCode, label: `${p.placeCode} — ${p.placeName}` }));
  }
  if (src === 'SUPPLY_CATEGORY') return (context.supplyCategories || []).map(c => ({ value: c, label: c }));
  if (src === 'TAX_RATE') return (context.rates || []).map(r => ({ value: r.id, label: `${r.supplyCategory} — ${r.ratePercentage}%` }));
  return [];
}

/** The field a derived field is derived from (by binding), or undefined. */
function sourceFieldOf(context: TaxFieldContextDto, f: JurisdictionFieldSchemaDto): JurisdictionFieldSchemaDto | undefined {
  if (!f.derivedFrom) return undefined;
  return (context.fields || []).find(s => (s.binding || '').toUpperCase() === (f.derivedFrom || '').toUpperCase());
}

function textOf(v: unknown): string { return v === null || v === undefined ? '' : String(v).trim(); }

/**
 * Client-side mirror of the server's TaxFieldSchemaValidator: required, pattern, static
 * options and basic types. Returns `{}` when valid, else `{ [fieldKey]: message }`.
 */
export function validateTaxFields(
  context: TaxFieldContextDto | null, values: TaxFieldValues, onlyKeys?: string[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!context) return errors;
  const fields = (context.fields || []).filter(f => !onlyKeys || onlyKeys.includes(f.fieldKey));
  for (const f of fields) {
    const raw = values[f.fieldKey];
    const text = textOf(raw);
    if (!text) {
      // A derived field is filled by its source: while the source has a value the source's own
      // problem (format, check digit, unknown code) is the message, not "state is required".
      const source = sourceFieldOf(context, f);
      const sourceFilled = !!source && !!textOf(values[source.fieldKey]);
      if (f.isRequired && !sourceFilled) errors[f.fieldKey] = `${f.label} is required.`;
      continue;
    }
    switch ((f.dataType || '').toUpperCase()) {
      case 'NUMBER':
        if (Number.isNaN(Number(text))) errors[f.fieldKey] = `${f.label} must be a number.`;
        break;
      case 'DATE':
        if (Number.isNaN(Date.parse(text))) errors[f.fieldKey] = `${f.label} must be a date.`;
        break;
      case 'SELECT': {
        const statics = staticValues(f.optionSource);
        if (statics && !statics.some(s => s.toUpperCase() === text.toUpperCase())) {
          errors[f.fieldKey] = `${f.label} must be one of ${statics.join(', ')}.`;
        }
        break;
      }
      default:
        if (f.validationPattern) {
          try {
            if (!new RegExp(f.validationPattern).test(text)) {
              errors[f.fieldKey] = `${f.label} is not in the expected format${f.helpText ? `: ${f.helpText}` : '.'}`;
            }
          } catch { /* a bad pattern is a seed defect; never block the user on it */ }
        }
    }
  }
  return errors;
}

/** A small info icon beside a label; the guidance shows on hover (and to screen readers) instead of under the field. */
const HelpIcon: React.FC<{ text: string }> = ({ text }) => (
  <span title={text} aria-label={text} role="img" tabIndex={0}
    style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '0.3rem', color: 'var(--text-muted)', cursor: 'help' }}>
    <IconSystem.info size={14} />
  </span>
);

/** What a derived field currently shows: the resolved label, or why the source does not resolve. */
interface DerivedState { label?: string; message?: string; pending?: boolean }

/**
 * Resolves every derived field of the context from its source value: debounced parse of the
 * number through the entity's jurisdiction, then the target value is set to what it resolves
 * to (or cleared) so the host's values and the server's derivation agree. Keyed on the source
 * value only — setting the target never re-triggers it.
 */
function useDerivedFields(
  context: TaxFieldContextDto | null, values: TaxFieldValues, onChange: (key: string, value: unknown) => void,
  parseNumber?: (number: string) => Promise<RegistrationNumberParseDto>,
): Record<string, DerivedState> {
  const [state, setState] = useState<Record<string, DerivedState>>({});
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;
  const parseRef = useRef(parseNumber); parseRef.current = parseNumber;
  const valuesRef = useRef(values); valuesRef.current = values;

  const derived = (context?.fields || []).filter(f => !!f.derivedFrom);
  // One string per source value so the effect's dependency stays a primitive.
  const sourceKey = derived.map(f => {
    const source = context ? sourceFieldOf(context, f) : undefined;
    return `${f.fieldKey}=${source ? textOf(values[source.fieldKey]) : ''}`;
  }).join('|');

  useEffect(() => {
    if (!context || derived.length === 0) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      for (const f of derived) {
        const source = sourceFieldOf(context, f);
        const number = source ? textOf(valuesRef.current[source.fieldKey]) : '';
        if (!number) {
          // Source empty: the field is an ordinary picker; leave whatever it holds.
          setState(p => (p[f.fieldKey] ? { ...p, [f.fieldKey]: {} } : p));
          continue;
        }
        setState(p => ({ ...p, [f.fieldKey]: { ...p[f.fieldKey], pending: true } }));
        try {
          const r = parseRef.current ? await parseRef.current(number) : await taxMetaService.parseRegistrationNumber(number);
          if (cancelled) return;
          // The state the code points at shows even while the number is mistyped (the message
          // says what is wrong); the server refuses the save until the number is right.
          const label = r.stateProvinceId ? `${r.placeCode} — ${r.stateName}` : undefined;
          const message = r.message || (r.stateProvinceId ? undefined : 'The number does not resolve to a state.');
          setState(p => ({ ...p, [f.fieldKey]: { label, message } }));
          // The field holds the statutory place code (the same vocabulary the documents use).
          const next = r.stateProvinceId ? (r.placeCode || '') : '';
          if (textOf(valuesRef.current[f.fieldKey]) !== next) onChangeRef.current(f.fieldKey, next);
        } catch {
          if (!cancelled) setState(p => ({ ...p, [f.fieldKey]: { message: 'Could not check the number.' } }));
        }
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, sourceKey]);

  return state;
}

const TaxFieldsPanel: React.FC<Props> = ({ context, values, onChange, errors, onlyKeys, bare, disabled, asyncSources, parseNumber }) => {
  const derivedState = useDerivedFields(context, values, onChange, parseNumber);
  if (!context || !context.jurisdiction) return null;
  const fields = (context.fields || []).filter(f => !onlyKeys || onlyKeys.includes(f.fieldKey));
  if (fields.length === 0) return null;

  const err = (key: string) => errors?.[key];

  const body = (
    <div className="form-grid-2">
      {fields.map(f => {
        const v = values[f.fieldKey];
        const message = err(f.fieldKey);
        // Guidance travels as an info icon beside the label (hover for the text) so it takes no
        // vertical space; only a problem is written under the field.
        const help = message ? <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{message}</div> : null;
        const source = sourceFieldOf(context, f);
        const hint = source ? `Taken from the ${source.label}: its first characters are the state code.` : f.helpText || '';
        const info = hint ? <HelpIcon text={hint} /> : null;
        const label = <>{f.label}{f.isRequired && <span className="text-danger"> *</span>}{info}</>;

        // Derived from the number while the number is filled: shown, not picked.
        if (source && textOf(values[source.fieldKey])) {
          const d = derivedState[f.fieldKey] || {};
          const shown = d.label ? d.label : d.pending ? 'Resolving…' : d.message ? 'Not a published state' : '…';
          return (
            <div key={f.fieldKey} className="form-group">
              <label className="form-label">{label}</label>
              <input type="text" className={`form-input${d.message ? ' is-invalid' : ''}`} value={shown} readOnly tabIndex={-1}
                title={`Taken from ${source.label}`} style={{ background: 'var(--bg-secondary)', cursor: 'default' }} />
              {d.message && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{d.message}</div>}
            </div>
          );
        }

        if (f.dataType === 'BOOL') {
          return (
            <div key={f.fieldKey} className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" id={`tax-${f.fieldKey}`} checked={!!v && String(v) !== 'false'} disabled={disabled}
                  onChange={e => onChange(f.fieldKey, e.target.checked)} />
                <label htmlFor={`tax-${f.fieldKey}`} style={{ fontSize: '0.8125rem', cursor: 'pointer' }}>{f.label}</label>{info}
              </div>
              {help}
            </div>
          );
        }
        if (f.dataType === 'SELECT') {
          const src = (f.optionSource || '').toUpperCase();
          const async = asyncSources?.[src];
          if (async) {
            const current = (v as string) || '';
            const seedLabel = current ? (async.labelFor?.(current) || current) : '';
            return (
              <div key={f.fieldKey} className="form-group">
                <SyutSelect label={label} isAsync loadOptions={async.load} defaultOptions cacheOptions={false}
                  seedOptions={current ? [{ value: current, label: seedLabel }] : undefined}
                  value={current}
                  onChange={val => onChange(f.fieldKey, (val as string) || '')}
                  isCreatable={!!async.allowFreeEntry}
                  onCreateOption={async.allowFreeEntry ? (input) => onChange(f.fieldKey, input.trim().toUpperCase()) : undefined}
                  formatCreateLabel={async.allowFreeEntry ? (input) => `Use "${input.trim().toUpperCase()}" (not in the master)` : undefined}
                  isClearable menuPortalTarget={document.body} isDisabled={disabled}
                  placeholder={async.allowFreeEntry ? 'Search, or type a code' : 'Search…'} />
                {help}
              </div>
            );
          }
          return (
            <div key={f.fieldKey} className="form-group">
              <SyutSelect label={label}
                options={optionsForField(context, f)} value={(v as string) || ''}
                onChange={val => onChange(f.fieldKey, (val as string) || '')}
                isClearable menuPortalTarget={document.body} isDisabled={disabled} />
              {help}
            </div>
          );
        }
        return (
          <div key={f.fieldKey} className="form-group">
            <label className="form-label">{label}</label>
            <input type={f.dataType === 'NUMBER' ? 'number' : f.dataType === 'DATE' ? 'date' : 'text'}
              className={`form-input${message ? ' is-invalid' : ''}`} value={(v as string) ?? ''} maxLength={100} disabled={disabled}
              onChange={e => onChange(f.fieldKey, e.target.value)} />
            {help}
          </div>
        );
      })}
    </div>
  );

  if (bare) return body;

  return (
    <div style={{
      border: '1px solid var(--form-border-color)',
      borderRadius: '0.5rem',
      padding: '0.75rem 1rem 0.5rem',
      marginTop: '0.5rem',
      background: 'var(--bg-secondary)',
    }}>
      <div style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--text-secondary)',
        marginBottom: '0.6rem',
      }}>
        Tax · {context.jurisdiction.jurisdictionName}
      </div>
      {body}
    </div>
  );
};

export default TaxFieldsPanel;
